<template>
  <div class="mc-video-history-new__header-wrapper">
    <div class="mc-video-history-new__filter-vehicle-wrapper">
      <FormulateInput
        v-bind="vehicleFields"
        v-model="licensePlateModel"
        class="mc-video-history-new__filter-vehicle"
        @update:fullObject="handleUpdateFullObjectVehicle"
      />
      <VideoStatusBadge v-if="vehicleIdValue" :isOnline="getVideoStatusIsOnline(vehicleIdValue)" />
    </div>
    <div class="mc-video-history-new__filter-period">
      <date-picker
        ref="periodePicker"
        v-model="periodModel"
        type="datetime"
        :placeholder="$t('global.action.selectDate')"
        format="DD MMM YYYY HH:mm:ss"
        value-type="YYYY-MM-DD HH:mm:ss"
        range-separator=" - "
        range
        :class="datePickerClasses"
        :confirm="false"
        :clearable="false"
        :disabled-date="disableFutureDates"
        :disabled="$store.state.vehicle.isLoadingVehicleVideos"
        :lang="datePickerLang"
        :shortcuts="[]"
        @pick="pickDate"
      >
        <template #sidebar>
          <button
            v-for="(shortcut, index) in makeShortcuts"
            :key="index"
            type="button"
            class="mx-btn mx-btn-text mx-btn-shortcut"
            :class="{ active: isShortcutActive(shortcut) }"
            @click="selectShortcut(shortcut)"
          >
            {{ shortcut.text }}
          </button>
        </template>
        <template #input>
          <input
            v-if="periodModel.length"
            name="date"
            type="text"
            autocomplete="off"
            :placeholder="$t('global.action.selectDate')"
            class="mx-input"
            :value="`${momentDMYH(periodModel[0])} - ${momentTime(periodModel[1])}`"
            :disabled="$store.state.vehicle.isLoadingVehicleVideos"
          />
        </template>
        <template #footer>
          <span class="mr-3">
            {{ momentDMYH(periodModel[0]) }} -
            {{ momentTime(periodModel[1]) }}
          </span>
          <b-button class="mx-2" variant="outline-primary" @click="cancelCalendar">
            {{ $t('global.action.cancel') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="$store.state.vehicle.isLoadingVehicleVideos"
            @click="appliedDatePicker"
          >
            {{ $t('global.action.apply') }}
          </b-button>
        </template>
      </date-picker>
    </div>
    <McButton
      variant="primary"
      :text="$t('global.apply')"
      :disabled="periodModel.length === 0 || !vehicleIdValue"
      @click="handleApply"
    />
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'
import { mapState, mapGetters } from 'vuex'
import VideoStatusBadge from '@/components/VideoStatusBadge'
import DatePicker from 'vue2-datepicker'
import datePickerLang from '@/helpers/date-picker-lang'
import McButton from '@/components/McButton'

import moment from 'moment'
moment.locale('id')

export default {
  name: 'HeaderSection',
  components: {
    VideoStatusBadge,
    DatePicker,
    McButton,
  },
  mixins: [Helper],
  data() {
    return {
      vehicleIdValue: null,
      licensePlateModel: null,
      periodModel: [],
      tempPeriod: [],
      intervalUpdateWsModalVehicle: null,
    }
  },

  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    ...mapState('videoHistory', [
      'vehicleStatuses',
      'vehicleTypes',
      'vgroups',
      'vehicleIdActive',
      'period',
      'modalOpen',
      'tempVehicleStatuses',
    ]),
    vehicleFields() {
      const tableFields = [
        {
          label: this.$t('report.fields.licensePlate'),
          key: 'vehicle.licensePlate',
          isSortable: true,
        },
        {
          label: 'Status Video',
          key: 'vehicleId',
          isSortable: false,
          isSlot: true,
          slotComponent: VideoStatusBadge,
          customProps: (item) => ({
            isOnline: this.getVideoStatusIsOnline(item.vehicleId),
          }),
        },
        {
          label: this.$t('report.fields.fleetType'),
          key: 'vehicle.vehicleType',
          isSortable: false,
          formatter: (value) => {
            return (
              (value.manufacturer ?? '-') +
              ' / ' +
              (value.model ?? '-') +
              ' / ' +
              (value.type ?? '-')
            )
          },
        },
        {
          label: this.$t('report.fields.vehicleGroup'),
          key: 'vehicle.vehicle2Vgroup',
          isSortable: false,
          formatter: (value) => {
            return value && value.length > 0 ? value.map((v) => v.vgroup.name).join(', ') : '-'
          },
        },
      ]
      return {
        name: 'license-plate-filter-video-history-new',
        isShow: true,
        type: 'trackvision-filter-value-help',
        placeholder: `${this.$t('global.action.choose')} ${this.$t('report.fields.licensePlate')}`,
        trackBy: 'vehicleId',
        displayedBy: 'vehicle.licensePlate',
        listGetter: (filter) => this.getVehicleList(filter),
        multiple: false,
        tableFields,
        rowCanClick: (vehicleId) => {
          return this.getVideoStatusIsOnline(vehicleId)
        },
        onModalShow: () => {
          this.$store.commit('videoHistory/setModalOpen', true)
        },
        onModalHidden: () => {
          this.$store.commit('videoHistory/setModalOpen', false)
        },
        filters: [
          {
            label: this.$t('report.fields.fleetType'),
            key: 'vehicleType',
            data: this.vehicleTypes.length
              ? this.vehicleTypes.map(({ vehicleTypeId, manufacturer, model, type }) => ({
                  name: (manufacturer ?? '-') + ' / ' + (model ?? '-') + ' / ' + (type ?? '-'),
                  id: vehicleTypeId,
                }))
              : [],
            model: [],
          },
          {
            label: this.$t('report.fields.vehicleGroup'),
            key: 'vgroup',
            data: this.vgroups.length
              ? this.vgroups.map(({ vgroupId, vgroup: { name } }) => ({ id: vgroupId, name }))
              : [],
            model: [],
          },
        ],
      }
    },
    getVideoStatusIsOnline() {
      return (vehicleId) => {
        const vehicleStatus = this.vehicleStatuses[vehicleId]
        if (!vehicleStatus) return false

        let secondEngineOn = 60
        let secondEngineOff = 600
        if (this.getFeatureFlagValue('trackvision_video_offline_acc_on')) {
          secondEngineOn = this.getFeatureFlagValue('trackvision_video_offline_acc_on')
        }
        if (this.getFeatureFlagValue('trackvision_video_offline_acc_off')) {
          secondEngineOff = this.getFeatureFlagValue('trackvision_video_offline_acc_off')
        }
        return !this.isDeviceTrackvisionOffline(
          secondEngineOn,
          secondEngineOff,
          vehicleStatus.lastReceive,
          vehicleStatus.engineOn
        )
      }
    },
    datePickerLang() {
      return datePickerLang()
    },
    datePickerClasses() {
      return {
        'mc-history-modal__list-date': true,
        'mc-videohistory-button-disabled': this.$store.state.vehicle.isLoadingVehicleVideos,
      }
    },

    makeShortcuts() {
      return [
        { text: this.$t('global.time.today'), onClick: () => this.shortcutsToday },
        { text: this.$t('global.time.yesterday'), onClick: () => this.shortcutsYesterday },
      ]
    },
    shortcutsToday() {
      const dateStart = new Date()
      dateStart.setHours(0, 0, 0, 0)
      return [dateStart, new Date()]
    },
    shortcutsYesterday() {
      const dateStart = new Date()
      const dateEnd = new Date()
      dateEnd.setTime(dateEnd.getTime() - 3600 * 1000 * 24)
      dateStart.setTime(dateStart.getTime() - 3600 * 1000 * 24)
      dateStart.setHours(0, 0, 0, 0)
      dateEnd.setHours(23, 59, 59, 999)
      return [dateStart, dateEnd]
    },
  },
  watch: {
    modalOpen(modalOpen) {
      if (!modalOpen) {
        clearInterval(this.intervalUpdateWsModalVehicle)
        return
      }
      this.updateDataVehicleStatuses(this.tempVehicleStatuses)
      this.intervalUpdateWsModalVehicle = setInterval(() => {
        this.updateDataVehicleStatuses(this.tempVehicleStatuses)
      }, 4000)
    },
  },
  async mounted() {
    let vehicleIdActive = null
    const startOfToday = moment().startOf('day').format('YYYY-MM-DD HH:mm')
    const now = moment().format('YYYY-MM-DD HH:mm')

    this.periodModel = [startOfToday, now]
    this.tempPeriod = [...this.periodModel]
    this.$store.commit(
      'videoHistory/setPeriod',
      this.periodModel.map((date) => this.formatDateToUtc(date))
    )

    if (this.$route.query.vehicleId) {
      vehicleIdActive = Number(this.$route.query.vehicleId)
      this.vehicleIdValue = vehicleIdActive
      await this.$nextTick()
    }

    if (this.$route.query.startTime && this.$route.query.endTime) {
      this.periodModel = [
        moment(this.$route.query.startTime).format('YYYY-MM-DD HH:mm'),
        moment(this.$route.query.endTime).format('YYYY-MM-DD HH:mm'),
      ]
      this.tempPeriod = [...this.periodModel]
    }

    while (Object.values(this.vehicleStatuses).length === 0) {
      await this.delay(1000)
    }

    this.licensePlateModel = this.vehicleStatuses[vehicleIdActive]?.vehicle.licensePlate

    this.$forceUpdate()
  },
  methods: {
    selectShortcut(shortcut) {
      const value = shortcut.onClick()
      if (value) this.$refs.periodePicker.emitValue(value)
    },
    isShortcutActive(shortcut) {
      if (!Array.isArray(this.periodModel) || this.periodModel.length !== 2) return false

      const value = shortcut.onClick()
      return value.every((date, index) =>
        moment(date).isSame(moment(this.periodModel[index]), 'day')
      )
    },
    async updateDataVehicleStatuses(tempVehicleStatuses) {
      Object.keys(tempVehicleStatuses).forEach((vehicleId) => {
        const { calculatedMotionStatus, engineOn, lastReceive } = tempVehicleStatuses[vehicleId]
        this.$store.commit('videoHistory/setVehicleStatusesByVehicleId', {
          vehicleId,
          data: { calculatedMotionStatus, engineOn, lastReceive },
        })
      })

      await this.$nextTick()

      this.$store.commit('videoHistory/resetTempVehicleStatuses')
    },
    disableFutureDates(date) {
      const currentDate = new Date()
      return date > currentDate
    },
    duplicatePeriodeForTemp() {
      this.tempPeriod = [...this.periodModel]
    },
    handleUpdateFullObjectVehicle(value) {
      this.vehicleIdValue = value.vehicleId
      this.$store.commit('videoHistory/setTempVehicleId', value.vehicleId)
    },
    appliedDatePicker() {
      this.duplicatePeriodeForTemp()
      this.closePopup()
    },
    closePopup() {
      if (this.$refs.periodePicker) {
        this.$refs.periodePicker.closePopup()
      }
    },
    cancelCalendar() {
      this.periodModel = [...this.tempPeriod]
      this.closePopup()
    },
    updatePeriodState(periode) {
      this.periodModel = [...periode]
    },
    async pickDate(date) {
      this.duplicatePeriodeForTemp()
      const pickerRefs = this.$refs.periodePicker.$refs.picker
      const overrideDefaultTime = moment(date)
        .set({
          hour: '23',
          minute: '59',
          second: '59',
        })
        .toDate()
      if (!pickerRefs.defaultTimeVisible) {
        this.updatePeriodState([
          moment(date).format('YYYY-MM-DD HH:mm'),
          moment(overrideDefaultTime).format('YYYY-MM-DD HH:mm'),
        ])
        await this.$nextTick()
        pickerRefs.value[0] = date
        pickerRefs.value[1] = overrideDefaultTime
        pickerRefs.openTimePanel()
      }
    },
    async handleApply() {
      this.$store.commit(
        'videoHistory/setPeriod',
        this.periodModel.map((date) => this.formatDateToUtc(date))
      )
      this.$store.commit('videoHistory/setVehicleIdActive', this.vehicleIdValue)

      this.$store.commit('videoHistory/resetVideoChannelActive')

      await this.$nextTick()

      this.$store.dispatch('videoHistory/getAllContentVideoHistory')
    },
    getVehicleList(opts = {}) {
      const search = opts.search?.trim().toLowerCase() ?? ''
      const vehicleTypeIds = opts.vehicleType ?? []
      const vgroupIds = opts.vgroup ?? []
      let orderBy = opts.orderBy ?? [],
        orderByLicensePlate = 'asc'
      if (typeof orderBy === 'string') {
        orderBy = JSON.parse(orderBy)
        orderByLicensePlate = orderBy[0]['vehicle.licensePlate'] ?? 'asc'
      }

      const vehicles = Object.values(this.vehicleStatuses)

      const filteredVehicles = vehicles
        .filter(({ vehicle }) => {
          const matchSearch = !search || vehicle?.licensePlate?.toLowerCase().includes(search)

          const matchVehicleType =
            !vehicleTypeIds.length || vehicleTypeIds.includes(vehicle?.vehicleType?.vehicleTypeId)

          const matchVgroup =
            !vgroupIds.length ||
            vehicle?.vehicle2Vgroup?.some(({ vgroupId }) => vgroupIds.includes(vgroupId))

          return matchSearch && matchVehicleType && matchVgroup
        })
        .sort((a, b) => {
          if (orderByLicensePlate === 'desc') {
            return b.vehicle.licensePlate
              .toLowerCase()
              .localeCompare(a.vehicle.licensePlate.toLowerCase())
          }
          const aOnline = this.getVideoStatusIsOnline(a.vehicleId)
          const bOnline = this.getVideoStatusIsOnline(b.vehicleId)

          if (aOnline !== bOnline) {
            return aOnline ? -1 : 1 // Online comes first
          }
          return a.vehicle.licensePlate
            .toLowerCase()
            .localeCompare(b.vehicle.licensePlate.toLowerCase())
        })

      return {
        data: filteredVehicles,
        ids: filteredVehicles.map(({ vehicleId }) => vehicleId),
        pagination: {
          page: 1,
          show: filteredVehicles.length,
          total: filteredVehicles.length,
        },
      }
    },
  },
}
</script>
